import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Alert,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Stack,
  Chip
} from '@mui/material';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import SendIcon from '@mui/icons-material/Send';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { submitAccessRequest, fetchUserAccessRequestStatus } from '../utils/apiClient';
import { AccessRequest } from '../types';
import { auth } from '../firebase';
import { logEvent } from '../analytics';

interface AccessDeniedPageProps {
  userEmail: string;
  onSignOut: () => void;
  onRefreshAccess?: () => Promise<void> | void;
}

const AccessDeniedPage: React.FC<AccessDeniedPageProps> = ({ userEmail, onSignOut, onRefreshAccess }) => {
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [existingRequest, setExistingRequest] = useState<AccessRequest | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Form fields
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const checkRequestStatus = useCallback(async () => {
    try {
      setCheckingStatus(true);
      const res = await fetchUserAccessRequestStatus();
      if (res.request) {
        setExistingRequest(res.request);
        if (res.request.status === 'approved' && onRefreshAccess) {
          // User was approved! Attempt to refresh permissions and load portal
          await onRefreshAccess();
        }
      }
    } catch (e) {
      console.warn('Could not check user access request status:', e);
    } finally {
      setCheckingStatus(false);
    }
  }, [onRefreshAccess]);

  useEffect(() => {
    document.title = "Jets '14 | Access Denied";

    // Set default name if user has a display name
    const currentUser = auth.currentUser;
    if (currentUser?.displayName) {
      setName(currentUser.displayName);
    } else if (userEmail) {
      setName(userEmail.split('@')[0]);
    }

    // Check if user already submitted a request
    checkRequestStatus();
  }, [userEmail, checkRequestStatus]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (onRefreshAccess) {
        await onRefreshAccess();
      } else {
        window.location.reload();
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleOpenDialog = () => {
    setSubmitError(null);
    setRequestDialogOpen(true);
    logEvent('open_access_request_dialog');
  };

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setSubmitError('Please enter your name.');
      return;
    }
    if (!reason.trim()) {
      setSubmitError('Please provide a reason or relationship to the team.');
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      const res = await submitAccessRequest({
        name: name.trim(),
        reason: reason.trim(),
        email: userEmail,
      });

      setExistingRequest(res.request);
      setSubmitSuccess(true);
      logEvent('submit_access_request_success');
      
      setTimeout(() => {
        setRequestDialogOpen(false);
      }, 1500);
    } catch (err: any) {
      console.error('Failed to submit access request:', err);
      setSubmitError(err.message || 'Failed to submit request. Please try again.');
      logEvent('submit_access_request_failed', { error: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ py: 6 }}>
      <Paper 
        elevation={4} 
        sx={{ 
          p: { xs: 3, sm: 5 }, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          borderRadius: 3,
          textAlign: 'center' 
        }}
      >
        <Box 
          sx={{ 
            width: 72, 
            height: 72, 
            borderRadius: '50%', 
            bgcolor: '#fee2e2', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            mb: 2 
          }}
        >
          <LockPersonIcon sx={{ fontSize: 40, color: '#dc2626' }} />
        </Box>

        <Typography component="h1" variant="h5" fontWeight="bold" gutterBottom>
          Access Denied
        </Typography>

        <Alert severity="error" sx={{ my: 2, width: '100%', textAlign: 'left' }}>
          The account <strong>{userEmail}</strong> is signed in but is not registered on any team roster or access list.
        </Alert>

        {checkingStatus ? (
          <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Checking access request status...</Typography>
          </Box>
        ) : existingRequest?.status === 'approved' ? (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2.5, 
              my: 2, 
              width: '100%', 
              bgcolor: '#f0fdf4', 
              borderColor: '#86efac', 
              borderRadius: 2,
              textAlign: 'left' 
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <CheckCircleOutlineIcon color="success" fontSize="small" />
              <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                Access Request Approved!
              </Typography>
              <Chip label="Approved" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Your access request has been approved by the administrator. You have been granted access to the Chicago Jets portal.
            </Typography>
            {existingRequest.assignedRoles && existingRequest.assignedRoles.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Assigned Roles:
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  {existingRequest.assignedRoles.map((role) => (
                    <Chip key={role} label={role} size="small" variant="outlined" color="primary" sx={{ height: 22, textTransform: 'capitalize' }} />
                  ))}
                </Stack>
              </Box>
            )}
            <Button
              variant="contained"
              color="success"
              fullWidth
              disabled={isRefreshing}
              onClick={handleRefresh}
              startIcon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
              disableElevation
              sx={{ mt: 1 }}
            >
              {isRefreshing ? 'Entering Portal...' : 'Enter Team Portal'}
            </Button>
          </Paper>
        ) : existingRequest?.status === 'pending' ? (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2.5, 
              my: 2, 
              width: '100%', 
              bgcolor: '#fefce8', 
              borderColor: '#fde047', 
              borderRadius: 2,
              textAlign: 'left' 
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <HourglassTopIcon color="warning" fontSize="small" />
              <Typography variant="subtitle2" fontWeight="bold" color="warning.dark">
                Access Request Pending Review
              </Typography>
              <Chip label="Pending" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Your access request has been sent to the team administrator (<strong>bwuest@gmail.com</strong>).
            </Typography>
            {existingRequest.reason && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>
                Reason submitted: "{existingRequest.reason}"
              </Typography>
            )}
          </Paper>
        ) : existingRequest?.status === 'rejected' ? (
          <Alert severity="warning" sx={{ my: 2, width: '100%', textAlign: 'left' }}>
            A previous access request for this email was not approved. If you believe this is in error, you may submit an updated request.
          </Alert>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            If you are a parent, coach, team family member, or club administrator, you can submit an access request to be reviewed by the team manager.
          </Typography>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%', mt: 2 }}>
          {(!existingRequest || existingRequest.status === 'rejected') && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<SendIcon />}
              onClick={handleOpenDialog}
              disableElevation
            >
              Request Access
            </Button>
          )}

          {existingRequest?.status === 'pending' && (
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              startIcon={<SendIcon />}
              onClick={handleOpenDialog}
            >
              Update Request
            </Button>
          )}

          {existingRequest?.status === 'approved' && (
            <Button
              variant="contained"
              color="success"
              fullWidth
              disabled={isRefreshing}
              onClick={handleRefresh}
              startIcon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : undefined}
              disableElevation
            >
              {isRefreshing ? 'Refreshing...' : 'Enter Team Portal'}
            </Button>
          )}

          <Button 
            variant="outlined" 
            color="inherit" 
            fullWidth 
            onClick={onSignOut}
          >
            Sign Out
          </Button>
        </Stack>
      </Paper>

      {/* REQUEST ACCESS DIALOG */}
      <Dialog 
        open={requestDialogOpen} 
        onClose={() => !isSubmitting && setRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmitRequest}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>
            {existingRequest ? 'Update Access Request' : 'Request Portal Access'}
          </DialogTitle>
          <DialogContent dividers>
            {submitSuccess ? (
              <Alert severity="success" icon={<CheckCircleOutlineIcon fontSize="inherit" />} sx={{ my: 1 }}>
                Access request submitted successfully! An administrator will review your request shortly.
              </Alert>
            ) : (
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                {submitError && (
                  <Alert severity="error">
                    {submitError}
                  </Alert>
                )}

                <Typography variant="body2" color="text.secondary">
                  Please let us know who you are and why you need access to the Chicago Jets 2014 team management portal.
                </Typography>

                <TextField
                  label="Email Address"
                  value={userEmail}
                  disabled
                  fullWidth
                  helperText="Your currently signed-in Google or email account."
                />

                <TextField
                  label="Your Full Name"
                  placeholder="e.g. Jane Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                  autoFocus
                />

                <TextField
                  label="Why do you want access / Relationship to team?"
                  placeholder="e.g. Bobby's Grandma, Assistant Coach, League Official, etc."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  multiline
                  rows={3}
                  fullWidth
                />
              </Stack>
            )}
          </DialogContent>
          {!submitSuccess && (
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setRequestDialogOpen(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
                disableElevation
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogActions>
          )}
        </form>
      </Dialog>
    </Container>
  );
};

export default AccessDeniedPage;
